import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient, useSession } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Account-level personal settings, surfaced as a dialog from the sidebar's
 * user menu (no longer a top-level page). Currently just a name editor;
 * email is read-only.
 */
export function AccountSettingsDialog({ open, onOpenChange }: Props) {
  const { data: session, refetch } = useSession();

  const sessionName = session?.user.name;
  const [name, setName] = useState(sessionName ?? "");
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const [prevSessionName, setPrevSessionName] = useState(sessionName);
  if (prevSessionName !== sessionName) {
    setPrevSessionName(sessionName);
    if (sessionName) setName(sessionName);
  }

  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setError("");
      setSavedFlash(false);
    }
  }

  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSavedFlash(false), 3000);
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      const { error: updateError } = await authClient.updateUser({ name });
      if (updateError) throw new Error(updateError.message ?? "Update failed");
    },
    onSuccess: () => {
      refetch();
      setError("");
      flashSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  const trimmed = name.trim();
  const dirty = trimmed !== (session?.user.name ?? "") && trimmed.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account settings</DialogTitle>
          <DialogDescription>
            Update your display name. Your email is tied to your account and
            can&apos;t be changed here.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="account-name">Name</Label>
            <Input
              id="account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div className="grid gap-2">
            <Label>Email</Label>
            <Input
              value={session?.user.email ?? ""}
              readOnly
              className="bg-muted text-muted-foreground"
            />
          </div>
          {error && <InlineError error={error} />}
          {savedFlash && <p className="text-sm text-green-600">Saved.</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!dirty}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
